import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const HodSupplyApplications = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Supply applications data
  const [supplyApplications, setSupplyApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [semesterFilter, setSemesterFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [selectedApplications, setSelectedApplications] = useState(new Set());

  // Statistics
  const [statistics, setStatistics] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    todaySubmissions: 0,
    uniqueSubjects: 0,
    uniqueStudents: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);



  // Fetch data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch supply applications data...');

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }



        // Fetch supply applications data
        console.log('📊 Fetching supply applications data...');
        const applicationsResponse = await fetch(`${API_LINK}/api/hod/supply-applications`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!applicationsResponse.ok) {
          throw new Error(`HTTP error! status: ${applicationsResponse.status}`);
        }

        const applicationsResult = await applicationsResponse.json();

        if (applicationsResult.success) {
          console.log('✅ Supply applications data loaded successfully');
          console.log(`📈 Found ${applicationsResult.data.length} supply applications`);

          setSupplyApplications(applicationsResult.data);
          setFilteredApplications(applicationsResult.data);

          // Calculate statistics
          const stats = {
            totalApplications: applicationsResult.data.length,
            pendingApplications: applicationsResult.data.filter(a => a.status === 'pending').length,
            approvedApplications: applicationsResult.data.filter(a => a.status === 'approved').length,
            rejectedApplications: applicationsResult.data.filter(a => a.status === 'rejected').length,
            todaySubmissions: applicationsResult.data.filter(a => {
              const today = new Date().toDateString();
              const appDate = new Date(a.created_at).toDateString();
              return today === appDate;
            }).length,
            uniqueSubjects: new Set(applicationsResult.data.map(a => a.subject_code)).size,
            uniqueStudents: new Set(applicationsResult.data.map(a => a.student_id)).size
          };

          setStatistics(stats);
        } else {
          throw new Error(applicationsResult.message || 'Failed to fetch supply applications');
        }

      } catch (err) {
        console.error('💥 Error loading supply applications data:', err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userInfo]);

  // Filter and sort applications
  useEffect(() => {
    let filtered = [...supplyApplications];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(application => application.status === statusFilter);
    }

    // Apply semester filter
    if (semesterFilter !== 'all') {
      filtered = filtered.filter(application => application.semester === semesterFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(application =>
        application.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        application.student_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        application.semester.toLowerCase().includes(searchTerm.toLowerCase()) ||
        application.academic_year.toLowerCase().includes(searchTerm.toLowerCase()) ||
        application.subject_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        application.subject_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'student':
          return a.student_name.localeCompare(b.student_name);
        case 'semester':
          return a.semester.localeCompare(b.semester);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'subject':
          return a.subject_name.localeCompare(b.subject_name);
        default:
          return 0;
      }
    });

    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [supplyApplications, statusFilter, searchTerm, semesterFilter, sortBy]);



  // Handle application action (approve/reject)
  const handleApplicationAction = async (applicationId, newStatus, notes = '', reviewerName = 'HOD') => {
    try {
      setActionLoading(true);
      setActionError(null);
      setActionSuccess(null);

      console.log(`🔄 Processing ${newStatus} for application ${applicationId}`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(`${API_LINK}/api/hod/supply-applications/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          notes: notes
        })
      });

      const result = await response.json();

      if (result.success) {
        console.log(`✅ Successfully ${newStatus} application ${applicationId}`);

        // Update local state
        setSupplyApplications(prev => prev.map(application =>
          application.id === applicationId ? {
            ...application,
            status: newStatus,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          } : application
        ));

        if (selectedApplication && selectedApplication.id === applicationId) {
          setSelectedApplication({
            ...selectedApplication,
            status: newStatus,
            review_notes: notes,
            reviewed_by: reviewerName,
            reviewed_at: new Date().toISOString()
          });
        }

        setActionSuccess(result.message);
        setReviewNotes('');

        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setActionError(result.message);
      }

    } catch (error) {
      console.error('💥 Error updating supply application:', error);
      setActionError(`Failed to perform action: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action) => {
    if (selectedApplications.size === 0) {
      setActionError('Please select applications to perform bulk action');
      return;
    }

    const confirmMessage = `Are you sure you want to ${action} ${selectedApplications.size} selected applications?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    console.log(`🔄 Performing bulk ${action} on ${selectedApplications.size} applications`);

    for (const applicationId of selectedApplications) {
      await handleApplicationAction(applicationId, action);
    }

    setSelectedApplications(new Set());
  };

  // Toggle application selection
  const toggleApplicationSelection = (applicationId) => {
    const newSelection = new Set(selectedApplications);
    if (newSelection.has(applicationId)) {
      newSelection.delete(applicationId);
    } else {
      newSelection.add(applicationId);
    }
    setSelectedApplications(newSelection);
  };

  // Select/deselect all applications
  const toggleAllSelection = () => {
    if (selectedApplications.size === currentApplications.length) {
      setSelectedApplications(new Set());
    } else {
      setSelectedApplications(new Set(currentApplications.map(application => application.id)));
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { color: '#ffc107', bg: '#fff3cd', icon: '⏳', text: 'Pending Review' };
      case 'approved':
        return { color: '#28a745', bg: '#d4edda', icon: '✅', text: 'Approved' };
      case 'rejected':
        return { color: '#dc3545', bg: '#f8d7da', icon: '❌', text: 'Rejected' };
      default:
        return { color: '#6c757d', bg: '#e2e3e5', icon: '❓', text: status };
    }
  };

  // Get unique semesters for filter
  const getUniqueSemesters = () => {
    const semesters = [...new Set(supplyApplications.map(application => application.semester))];
    return semesters.sort();
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentApplications = filteredApplications.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-supply-applications">
      {({ hodData, isMobile, handleLogout }) => (
        <>
          {/* Modal CSS */}
          <style>
            {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 1rem;
            box-sizing: border-box;
          }
          
          .modal-content {
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
          }
        `}
          </style>

          {/* Loading Component */}
          {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Supply Applications...</p>
            </div>
          )}

          {/* Error Component */}
          {error && !loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem'
            }}>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '3rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: '400px',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{ fontSize: '4rem', color: '#ff6b6b', marginBottom: '1.5rem' }}>⚠️</div>
                <h3 style={{ color: '#333', margin: '0 0 1rem 0', fontSize: '1.4rem', fontWeight: 600 }}>Unable to Load Supply Applications</h3>
                <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem', lineHeight: 1.5 }}>{error}</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => window.location.reload()} style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Try Again</button>
                  <button onClick={handleLogout} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 500 }}>Logout</button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && (
            <>
              {/* Page Header */}
              <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                borderLeft: '4px solid #238548'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <h2 style={{
                      color: '#238548',
                      margin: '0 0 0.5rem 0',
                      fontSize: '1.8rem',
                      fontWeight: 'bold'
                    }}>
                      📦 Supply Exam Applications
                    </h2>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '1rem'
                    }}>
                      Review and manage student supply exam applications for {hodData?.department || 'your department'}
                    </p>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <strong>Total: {statistics.totalApplications}</strong>
                    </div>

                    <div style={{
                      background: '#fff3cd',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      Pending: {statistics.pendingApplications}
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {[
                  {
                    icon: '📦',
                    title: 'Total Applications',
                    value: statistics.totalApplications,
                    subtitle: 'All time applications',
                    color: '#238548'
                  },
                  {
                    icon: '⏳',
                    title: 'Pending Review',
                    value: statistics.pendingApplications,
                    subtitle: 'Awaiting your review',
                    color: '#ffc107'
                  },
                  {
                    icon: '✅',
                    title: 'Approved',
                    value: statistics.approvedApplications,
                    subtitle: 'Successfully approved',
                    color: '#28a745'
                  },
                  {
                    icon: '❌',
                    title: 'Rejected',
                    value: statistics.rejectedApplications,
                    subtitle: 'Rejected applications',
                    color: '#dc3545'
                  },
                  {
                    icon: '📅',
                    title: 'Today',
                    value: statistics.todaySubmissions,
                    subtitle: 'Submitted today',
                    color: '#6f42c1'
                  },
                  {
                    icon: '📚',
                    title: 'Subjects',
                    value: statistics.uniqueSubjects,
                    subtitle: 'Unique subjects',
                    color: '#17a2b8'
                  }
                ].map((stat, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}>
                    <div style={{
                      fontSize: '2rem',
                      color: stat.color,
                      marginBottom: '0.5rem'
                    }}>
                      {stat.icon}
                    </div>
                    <h3 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '0.9rem' }}>{stat.title}</h3>
                    <p style={{
                      fontSize: '1.8rem',
                      fontWeight: 'bold',
                      color: stat.color,
                      margin: '0.25rem 0'
                    }}>
                      {stat.value}
                    </p>
                    <p style={{
                      color: '#666',
                      margin: 0,
                      fontSize: '0.8rem'
                    }}>
                      {stat.subtitle}
                    </p>
                  </div>
                ))}
              </div>

              {/* Action Messages */}
              {(actionSuccess || actionError) && (
                <div style={{
                  background: actionSuccess ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${actionSuccess ? '#c3e6cb' : '#f5c6cb'}`,
                  color: actionSuccess ? '#155724' : '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>
                    {actionSuccess ? '✅' : '❌'}
                  </span>
                  {actionSuccess || actionError}
                </div>
              )}

              {/* Filters and Search */}
              <div style={{
                background: '#ffffff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  alignItems: 'end'
                }}>
                  {/* Search */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      🔍 Search
                    </label>
                    <input
                      type="text"
                      placeholder="Search by student, semester, subject..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                      }}
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Status Filter
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Semester Filter */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Semester Filter
                    </label>
                    <select
                      value={semesterFilter}
                      onChange={(e) => setSemesterFilter(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="all">All Semesters</option>
                      {getUniqueSemesters().map(semester => (
                        <option key={semester} value={semester}>{semester}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="student">Student Name</option>
                      <option value="semester">Semester</option>
                      <option value="status">Status</option>
                      <option value="subject">Subject</option>
                    </select>
                  </div>

                  {/* Clear Filters */}
                  <div>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setSemesterFilter('all');
                        setSortBy('newest');
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#f8f9fa',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        fontWeight: '500',
                        color: '#333',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e9ecef';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f8f9fa';
                      }}
                    >
                      🗑️ Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedApplications.size > 0 && (
                <div style={{
                  background: '#e3f2fd',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #bbdefb'
                }}>
                  <span style={{ fontWeight: '500', color: '#1976d2' }}>
                    {selectedApplications.size} application(s) selected
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleBulkAction('approved')}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#28a745',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ✅ Approve All
                    </button>
                    <button
                      onClick={() => handleBulkAction('rejected')}
                      disabled={actionLoading}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#dc3545',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}
                    >
                      ❌ Reject All
                    </button>
                  </div>
                </div>
              )}

              {/* Supply Applications List */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                marginBottom: '2rem'
              }}>
                {/* Table Header */}
                <div style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedApplications.size === currentApplications.length && currentApplications.length > 0}
                    onChange={toggleAllSelection}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontWeight: '500', color: '#333' }}>
                    Select All ({currentApplications.length} applications)
                  </span>
                </div>

                {currentApplications.length > 0 ? (
                  currentApplications.map((application, index) => {
                    const statusInfo = getStatusInfo(application.status);

                    return (
                      <div
                        key={application.id}
                        style={{
                          padding: '1.5rem',
                          borderBottom: index < currentApplications.length - 1 ? '1px solid #ecf0f1' : 'none',
                          transition: 'all 0.3s ease',
                          backgroundColor: '#ffffff'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'start'
                        }}>
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            checked={selectedApplications.has(application.id)}
                            onChange={() => toggleApplicationSelection(application.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              marginTop: '0.25rem'
                            }}
                          />

                          {/* Application Content */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'start',
                              marginBottom: '1rem',
                              flexWrap: 'wrap',
                              gap: '1rem'
                            }}>
                              <div style={{ flex: 1, minWidth: '300px' }}>
                                <h3 style={{
                                  margin: '0 0 0.5rem 0',
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  color: '#333'
                                }}>
                                  {application.student_name} ({application.student_id})
                                </h3>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#666',
                                  fontSize: '0.9rem'
                                }}>
                                  <strong>{application.semester}</strong> • {application.academic_year}
                                </p>
                                <p style={{
                                  margin: '0 0 0.5rem 0',
                                  color: '#238548',
                                  fontSize: '0.95rem',
                                  fontWeight: '500'
                                }}>
                                  📚 {application.subject_code} - {application.subject_name}
                                </p>
                              </div>

                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '0.5rem',
                                minWidth: '150px'
                              }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '15px',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold',
                                  backgroundColor: statusInfo.bg,
                                  color: statusInfo.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}>
                                  {statusInfo.icon} {statusInfo.text}
                                </span>

                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#999',
                                  textAlign: 'right'
                                }}>
                                  <div>📅 {formatDate(application.created_at)}</div>
                                  <div>🕒 {formatTime(application.created_at)}</div>
                                </div>
                              </div>
                            </div>

                            {/* Review Notes */}
                            {application.review_notes && (
                              <div style={{
                                background: '#f8f9fa',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                marginBottom: '1rem',
                                border: '1px solid #e9ecef'
                              }}>
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: '#666',
                                  marginBottom: '0.25rem'
                                }}>
                                  Review by {application.reviewed_by} • {formatDate(application.reviewed_at)}
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#333' }}>
                                  {application.review_notes}
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap'
                            }}>
                              <button
                                onClick={() => {
                                  setSelectedApplication(application);
                                  setShowDetailModal(true);
                                  setActionError(null);
                                  setActionSuccess(null);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#17a2b8',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  fontWeight: '500'
                                }}
                              >
                                👁️ View Details
                              </button>

                              {application.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApplicationAction(application.id, 'approved')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#28a745',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ✅ Approve
                                  </button>

                                  <button
                                    onClick={() => handleApplicationAction(application.id, 'rejected')}
                                    disabled={actionLoading}
                                    style={{
                                      padding: '0.5rem 1rem',
                                      background: '#dc3545',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                      fontWeight: '500',
                                      opacity: actionLoading ? 0.7 : 1
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#666'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <h3 style={{ margin: '0 0 1rem 0' }}>No Supply Applications Found</h3>
                    <p style={{ margin: 0 }}>
                      {searchTerm || statusFilter !== 'all' || semesterFilter !== 'all'
                        ? 'Try adjusting your search filters'
                        : 'No supply exam applications have been submitted yet'
                      }
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === 1 ? '#f8f9fa' : '#238548',
                      color: currentPage === 1 ? '#6c757d' : '#ffffff',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    ← Previous
                  </button>

                  <span style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #238548',
                      borderRadius: '8px',
                      background: currentPage === totalPages ? '#f8f9fa' : '#238548',
                      color: currentPage === totalPages ? '#6c757d' : '#ffffff',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}


              {/* Detail Modal */}
              {showDetailModal && selectedApplication && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    {/* Modal Header */}
                    <div style={{
                      background: '#238548',
                      color: '#ffffff',
                      padding: '1.5rem 2rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                        📦 Supply Application Details
                      </h2>
                      <button
                        onClick={() => setShowDetailModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ffffff',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          lineHeight: 1
                        }}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '2rem' }}>
                      {/* Student & Application Info */}
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1.5rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                          gap: '1.5rem'
                        }}>
                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Student Information</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Name:</strong> {selectedApplication.student_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Student ID:</strong> {selectedApplication.student_id}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Status:</strong>
                              <span style={{
                                marginLeft: '0.5rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: getStatusInfo(selectedApplication.status).bg,
                                color: getStatusInfo(selectedApplication.status).color
                              }}>
                                {getStatusInfo(selectedApplication.status).icon} {getStatusInfo(selectedApplication.status).text}
                              </span>
                            </div>
                          </div>

                          <div>
                            <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Application Details</h3>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Semester:</strong> {selectedApplication.semester}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Academic Year:</strong> {selectedApplication.academic_year}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Subject Code:</strong> {selectedApplication.subject_code}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Subject Name:</strong> {selectedApplication.subject_name}
                            </div>
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Submitted:</strong> {formatDate(selectedApplication.created_at)} at {formatTime(selectedApplication.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Review Section */}
                      {selectedApplication.status === 'pending' && (
                        <div style={{
                          background: '#fff3cd',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          marginBottom: '1.5rem',
                          border: '1px solid #ffeaa7'
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review Action</h3>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add review notes (optional)..."
                            rows={3}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '2px solid #e9ecef',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              resize: 'vertical',
                              marginBottom: '1rem'
                            }}
                          />
                          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleApplicationAction(selectedApplication.id, 'approved', reviewNotes)}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#28a745',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '✅ Approve Application'}
                            </button>

                            <button
                              onClick={() => handleApplicationAction(selectedApplication.id, 'rejected', reviewNotes)}
                              disabled={actionLoading}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#dc3545',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                opacity: actionLoading ? 0.7 : 1
                              }}
                            >
                              {actionLoading ? '⏳ Processing...' : '❌ Reject Application'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing Review */}
                      {selectedApplication.review_notes && (
                        <div style={{
                          background: selectedApplication.status === 'approved' ? '#d4edda' : '#f8d7da',
                          padding: '1.5rem',
                          borderRadius: '8px',
                          border: `1px solid ${selectedApplication.status === 'approved' ? '#c3e6cb' : '#f5c6cb'}`
                        }}>
                          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>Review History</h3>
                          <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                            Reviewed by <strong>{selectedApplication.reviewed_by}</strong> on {formatDate(selectedApplication.reviewed_at)}
                          </div>
                          <p style={{ margin: 0, fontSize: '1rem', color: '#333' }}>
                            {selectedApplication.review_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodSupplyApplications;